/**
 * ShippingAgentsSettings component.
 * @module components/manage/ShippingAgentsSettings/ShippingAgentsSettings
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Button, Container, Segment, Table } from 'semantic-ui-react';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import { default as Toolbar } from '@plone/volto/components/manage/Toolbar/Toolbar';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import { useClient } from '@plone/volto/hooks/client/useClient';
import backSVG from '@plone/volto/icons/back.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import {
  deleteShippingAgentSettings,
  listShippingAgentsSettings,
} from '../../../actions/shop/shipping_agents_settings';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  delete: {
    id: 'Delete',
    defaultMessage: 'Delete',
  },
  shippingAgentsSettings: {
    id: 'Shipping Agents Settings',
    defaultMessage: 'Shipping Agents Settings',
  },
});

/**
 * Component to edit the annotations.
 * @function ShippingAgentsSettings
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ShippingAgentsSettings = ({ location }) => {
  const { pathname } = location;
  const baseUrl = getBaseUrl(pathname);
  const dispatch = useDispatch();
  const isClient = useClient();
  const intl = useIntl();
  const availableShippingAgentsSettings = useSelector(
    (state) =>
      state.shippingAgentsSettings.entries.filter((entry) => !entry.configured),
    shallowEqual,
  );
  const configuredShippingAgentsSettings = useSelector(
    (state) =>
      state.shippingAgentsSettings.entries.filter((entry) => entry.configured),
    shallowEqual,
  );
  const title = useSelector((state) => state.content.data.title);

  const statusError = useSelector(
    (state) => state.shippingAgentsSettings?.list?.error?.status,
  );

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      dispatch(listShippingAgentsSettings(baseUrl));
    }
    return () => {
      mounted = false;
    };
  }, [baseUrl, dispatch]);

  const deleteShipAgSettings = async (name) => {
    await dispatch(deleteShippingAgentSettings(baseUrl, name));
    await dispatch(listShippingAgentsSettings(baseUrl));
  };

  if (isClient && statusError === 401) {
    return <Unauthorized />;
  }

  return (
    <Container id="page-shipping-agents">
      <Helmet title={intl.formatMessage(messages.shippingAgentsSettings)} />
      <Segment.Group raised>
        <Segment className="primary">
          <FormattedMessage
            id="Shipping Agents Settings of {title}"
            defaultMessage="Shipping Agents Settings of {title}"
            values={{
              title: <q>{title}</q>,
            }}
          />
        </Segment>
        {availableShippingAgentsSettings.length > 0 && (
          <>
            <Segment secondary>
              <FormattedMessage
                id="You can see the available Shipping Agents below."
                defaultMessage="You can see the available Shipping Agents below."
              />
            </Segment>
            <Table celled padded striped attached>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    <FormattedMessage id="Name" defaultMessage="Name" />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {availableShippingAgentsSettings.map(
                  (shippingAgentSettings) => (
                    <Table.Row key={shippingAgentSettings.name}>
                      <Table.Cell>
                        <Link
                          to={`${baseUrl}/shipping-agent-settings/${shippingAgentSettings.name}`}
                        >
                          {shippingAgentSettings.title}
                        </Link>
                      </Table.Cell>
                    </Table.Row>
                  ),
                )}
              </Table.Body>
            </Table>
          </>
        )}
        {configuredShippingAgentsSettings && (
          <>
            <Segment secondary>
              <FormattedMessage
                id="You can see the configured Shipping Agents below."
                defaultMessage="You can see the configured Shipping Agents below."
              />
            </Segment>
            <Table celled padded striped attached>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    <FormattedMessage id="Name" defaultMessage="Name" />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {configuredShippingAgentsSettings.map(
                  (shippingAgentSettings) => (
                    <Table.Row key={shippingAgentSettings.name}>
                      <Table.Cell>
                        <Link
                          to={`${baseUrl}/shipping-agent-settings/${shippingAgentSettings.name}`}
                        >
                          {shippingAgentSettings.title}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          className="cancel"
                          aria-label={intl.formatMessage(messages.delete)}
                          onClick={() =>
                            deleteShipAgSettings(shippingAgentSettings.name)
                          }
                        >
                          <Icon
                            name={deleteSVG}
                            className="circled"
                            size="10px"
                            title={intl.formatMessage(messages.delete)}
                          />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ),
                )}
              </Table.Body>
            </Table>
          </>
        )}
      </Segment.Group>
      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link className="item" to={baseUrl}>
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ShippingAgentsSettings.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default ShippingAgentsSettings;
